// TableFilterGroup is a manager of TableFilter objects
//  This allows for adding and removing TableFilter objects (up to 10)
//  A Filter button is displayed, upon pressing (or hitting Enter in a filter input field)
//    the props.onFilter callback is called to alert the owner that a filter was requested
//    This callback provides all chosen filter options derived from the TableFilter components
//
// TableFilter is a single filter UI to choose a field,
//   an operator for that field (based on datatype)
//   and a value for the filter (based on datatype)
// Works with string, number, date, and boolean values

import { Autocomplete, FormControl, InputLabel, MenuItem, Select, Switch, TextField, InputAdornment, SelectChangeEvent, IconButton, Button, Input, Stack } from "@mui/material";
import { AddCircle, Clear, FilterAlt, Pin, Search } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { numberFromTextInput } from "system/Utilities";
import { useTranslation } from "react-i18next";

export interface TableFilterField {
  type: "string" | "number" | "boolean" | "datetime" | "date" | "object",
  title: string,
  id: string,
  allows_null?: boolean
}

export interface TableFilterProps {
  id: string,
  fieldOptions: TableFilterField[],
  onChange?: (id: string, field: TableFilterField, operator: string | null, input: string | null) => void,
  onSubmit?: () => void,
  initialField?: TableFilterField,
  initialOp?: string,
  initialSearch?: string
}

export function TableFilter(props: TableFilterProps) {
  let [currentField, setCurrentField] = useState<TableFilterField | null>(props.initialField || null);
  let [currentOp, setCurrentOp] = useState(props.initialOp || "=");
  let [currentSearch, setCurrentSearch] = useState<string>(props.initialSearch || "");

  let filterOptions: JSX.Element | null = null;
  const { i18n } = useTranslation();

  const onFieldChange = (event: React.SyntheticEvent<Element, Event>, field: TableFilterField | null) => {
    setCurrentField(field);
    if (field?.type === "string" || field?.type === "object") {
      currentOp = "contains";
    } else currentOp = "=";
    setCurrentOp(currentOp);

    if (field?.type === "datetime" || field?.type === "date") {
      let v = new Date().toISOString();

      // For date, chop off the time portion
      if (field?.type === "date")
        v = v.slice(0, 10);

      setCurrentSearch(v);
      // datetime defaults to populating the filter with "now", so we can go ahead and enable the filter
      props.onChange?.(props.id, field, currentOp, v);
    } else if (field?.type === "boolean") {
      // Boolean also should enable filtering immediately
      setCurrentSearch("0");
      props.onChange?.(props.id, field, currentOp, "0");
    } else {
      setCurrentSearch("");
    }
  }

  const onBooleanChange = (event: any): void => {
    let val = event.target.checked ? "1" : "0";
    setCurrentSearch(val);
    props.onChange?.(props.id, currentField as TableFilterField, currentOp, val);
  }

  const onSearchChange = (event: any): void => {
    let val = event.target.value.toString();
    if (currentField?.type === "number") {
      val = numberFromTextInput(event.target.value);
    }

    setCurrentSearch(val);
    props.onChange?.(props.id, currentField as TableFilterField, currentOp, val);
  }

  const onDatetimeChange = (v: Dayjs | null) => {
    try {
      if (v === null) {
        v = dayjs();
      }
      const asString = v.toISOString();

      setCurrentSearch(asString);
      props.onChange?.(props.id, currentField as TableFilterField, currentOp, asString);
    } catch (err) {
      // Error, probably bad date, ignore
    }
  }

  const onTextFieldKeydown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // This just looks for Enter to trigger filter like the Button
    if (event.key === 'Enter' || event.key === 'Return') {
      if (props.onSubmit) {
        event.stopPropagation();
        event.preventDefault();
        props.onSubmit();
      }
    }
  }

  const handleOpChange = (e: SelectChangeEvent<string>) => {
    setCurrentOp(e.target.value)
    if (currentSearch || e.target.value === "null" || e.target.value === "not-null") {
      props.onChange?.(props.id, currentField as TableFilterField, e.target.value, currentSearch);
    }
  }

  if (currentField?.type === "number") {
    filterOptions = <div>
      <FormControl variant="standard">
        <InputLabel>{i18n.t("filterable-table.operator")}</InputLabel>
        <Select
          sx={{ width: '14ch' }}
          size="small"
          onChange={handleOpChange}
          value={currentOp} >
          <MenuItem value={"="}>{i18n.t("filterable-table.op.equals")}</MenuItem>
          <MenuItem value={">="}>{i18n.t("filterable-table.op.at-least")}</MenuItem>
          <MenuItem value={"<="}>{i18n.t("filterable-table.op.at-most")}</MenuItem>
          <MenuItem value={">"}>{i18n.t("filterable-table.op.more-than")}</MenuItem>
          <MenuItem value={"<"}>{i18n.t("filterable-table.op.less-than")}</MenuItem>
          <MenuItem value={"!="}>{i18n.t("filterable-table.op.not-equal")}</MenuItem>
          {currentField?.allows_null === true &&
            <MenuItem value={"null"}>{i18n.t("filterable-table.op.is-null")}</MenuItem>}
          {currentField?.allows_null === true &&
            <MenuItem value={"not-null"}>{i18n.t("filterable-table.op.not-null")}</MenuItem>
          }
        </Select>
      </FormControl>
      {currentOp !== "null" && currentOp !== "not-null" &&
        <TextField autoComplete="off" sx={{ width: '14ch' }} variant="standard" style={{ paddingTop: "13px" }}
        slotProps={{
          input: {
            //type: "number", // Intentionally not set so that we can remove non-numerics in a cross-browser way
            inputMode: 'numeric',
            onChange: onSearchChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
            startAdornment: <InputAdornment position="start"><Pin /></InputAdornment>
          }
        }}
        onKeyDown={onTextFieldKeydown}
        value={currentSearch} />
      }
    </div>
  }
  else if (currentField?.type === "boolean") {
    filterOptions = <FormControl style={{ marginLeft: "0.5rem", marginTop: "0.75rem" }}>
      <Switch onChange={onBooleanChange} />
    </FormControl>
  }
  else if (currentField?.type === "datetime" || currentField?.type === "date") {
    filterOptions = <div>
      <FormControl variant="standard">
        <InputLabel>{i18n.t("filterable-table.operator")}</InputLabel>
        <Select
          sx={{ width: '14ch' }}
          size="small"
          onChange={handleOpChange}
          value={currentOp} >
          <MenuItem value={"="}>{i18n.t("filterable-table.op.date-on")}</MenuItem>
          <MenuItem value={">="}>{i18n.t("filterable-table.op.on-or-after")}</MenuItem>
          <MenuItem value={"<="}>{i18n.t("filterable-table.op.on-or-before")}</MenuItem>
          <MenuItem value={">"}>{i18n.t("filterable-table.op.after")}</MenuItem>
          <MenuItem value={"<"}>{i18n.t("filterable-table.op.before")}</MenuItem>
          <MenuItem value={"!="}>{i18n.t("filterable-table.op.date-not-on")}</MenuItem>
          {currentField?.allows_null === true &&
            <MenuItem value={"null"}>{i18n.t("filterable-table.op.is-null")}</MenuItem>}
          {currentField?.allows_null === true &&
            <MenuItem value={"not-null"}>{i18n.t("filterable-table.op.not-null")}</MenuItem>
          }
        </Select>
      </FormControl>
      {currentOp !== "null" && currentOp !== "not-null" &&
        <DatePicker
          value={dayjs(currentSearch)}
          onChange={onDatetimeChange}
          minDate={dayjs("2007-01-01")}
          slotProps={{
            textField: {
              variant: "standard",
              sx: { width: '14ch' },
              style: { paddingTop: "13px" },
              onKeyDown: onTextFieldKeydown
            }
          }}
        />}
    </div>
  }
  else if (currentField?.type === "string" || currentField?.type === "object") {
    filterOptions = <div>
      <FormControl variant="standard">
        <InputLabel>{i18n.t("filterable-table.operator")}</InputLabel>
        <Select
          sx={{ width: '14ch' }}
          size="small"
          onChange={handleOpChange}
          value={currentOp} >
          <MenuItem value={"contains"}>{i18n.t("filterable-table.op.contains")}</MenuItem>
          <MenuItem value={"not-contain"}>{i18n.t("filterable-table.op.doesnt-contain")}</MenuItem>
          <MenuItem value={"begins"}>{i18n.t("filterable-table.op.starts-with")}</MenuItem>
          <MenuItem value={"ends"}>{i18n.t("filterable-table.op.ends-with")}</MenuItem>
          <MenuItem value={"="}>{i18n.t("filterable-table.op.equals")}</MenuItem>
          <MenuItem value={"!="}>{i18n.t("filterable-table.op.not-equal")}</MenuItem>
          {currentField?.allows_null === true &&
            <MenuItem value={"null"}>{i18n.t("filterable-table.op.is-null")}</MenuItem>}
          {currentField?.allows_null === true &&
            <MenuItem value={"not-null"}>{i18n.t("filterable-table.op.not-null")}</MenuItem>
          }
        </Select>
      </FormControl>
      {currentOp !== "null" && currentOp !== "not-null" &&
        <TextField autoComplete="off" variant="standard" style={{ paddingTop: "13px" }} value={currentSearch}
          onChange={onSearchChange}
          onKeyDown={onTextFieldKeydown}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
            }
          }}
        />}
    </div>
  } else if(currentField) {
    console.error(`Unknown field type in TableFilter: ${currentField?.type}`);
    Sentry.captureMessage(`Unknown field type in TableFilter: ${currentField?.type}`);
  }

  return (
    <Stack direction="row" sx={{ paddingTop: 0.5 }}>
      <Autocomplete openOnFocus autoComplete autoHighlight
        onChange={onFieldChange}
        value={currentField}
        size="small"
        options={props.fieldOptions}
        getOptionLabel={(option: TableFilterField) => option.title}
        style={{ width: 200 }}
        isOptionEqualToValue={(option: TableFilterField, value: TableFilterField): boolean => option && value && option.id === value.id}
        renderInput={(params: any) => <TextField autoComplete="off" {...params} label={i18n.t("filterable-table.filter-by-field")} variant="standard" />}
      />

      {filterOptions}

    </Stack>
  );
}


export interface TableFilterState {
  id: string,
  op: string,
  value: string
};

export interface TableFilterGroupProps {
  filterOptions: TableFilterField[],
  initialState?: Record<string, TableFilterState>,
  initialSearch?: string,
  onFilter?: (search: string, filterState: Record<string, TableFilterState>) => void
}

export function TableFilterGroup(props: TableFilterGroupProps) {
  let [filterComponents, setFilterComponents] = useState<Array<JSX.Element>>([]);
  let [filterCounter, setFilterCounter] = useState(0);
  let [filterEnabled, setFilterEnabled] = useState(false);
  let [filterState, setFilterState] = useState<Record<string, TableFilterState>>(props.initialState || {});
  let [searchInput, setSearchInput] = useState(props.initialSearch || "");
  const { i18n } = useTranslation();

  const onSubmit = useCallback((_searchInput?: string): void => {
    if (_searchInput === undefined)
      _searchInput = searchInput;

    setFilterEnabled(false);
    props.onFilter?.(_searchInput, filterState);
  }, [filterState, props, searchInput]);

  const handleFilterClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    onSubmit();
  }

  const handleFilterChange = useCallback((id: string, field: TableFilterField, operator: string | null, input: string | null): void => {
    if (operator === null || (operator !== "null" && operator !== "not-null" && (input === null || input === ""))) {
      delete filterState[id];
    } else {
      filterState[id] = {
        id: field.id,
        op: operator as string,
        value: input as string
      }
    }

    setFilterState(filterState);
    setFilterEnabled(Object.keys(filterState).length > 0);
  }, [filterState]);

  useEffect(() => {
    if (props.initialState && filterCounter === 0) {
      let components = [];
      for (const value of Object.values(props.initialState)) {
        const initialField = props.filterOptions.find((el: TableFilterField) => value.id === el.id);
        if (initialField) {
          const id = "filter" + components.length.toString();
          components.push(<TableFilter id={id} fieldOptions={props.filterOptions} key={id} initialField={initialField} initialOp={value.op} initialSearch={value.value} onChange={handleFilterChange} onSubmit={onSubmit} />);
        }
      }
      setFilterCounter(components.length);
      setFilterComponents(components);
    }
  }, [handleFilterChange, onSubmit, props.filterOptions, props.initialState, filterCounter]);

  const handleAddFilter = useCallback(() => {
    let id = "filter" + filterCounter.toString();

    filterComponents.push(<TableFilter id={id} fieldOptions={props.filterOptions} key={id} onChange={handleFilterChange} onSubmit={onSubmit} />);
    setFilterComponents(filterComponents);
    setFilterCounter((prevState: number) => {
      return prevState + 1;
    });
  }, [filterCounter, handleFilterChange, onSubmit, props.filterOptions, filterComponents]);

  const handleRemoveFilter = useCallback((key: string) => {
    let wasPopulated = false;
    if (key && key in filterState) {
      // Save whether or not this filter had an ID set (field picked)
      // If it does, we'll allow reload, or do it if we remove all
      wasPopulated = Boolean(filterState[key].id);
      delete filterState[key];
      setFilterState({ ...filterState });
    }

    let filteredFilters = filterComponents.filter((el: JSX.Element) => el.key !== key) || [];
    setFilterComponents(filteredFilters);

    if (wasPopulated) {
      // Trigger a reload after a filter was removed
      onSubmit();
    }
  }, [filterComponents, filterState, onSubmit]);

  return (
    <>
      <Box>
        <Input style={{ marginTop: 8, marginLeft: 4 }}
          autoFocus
          startAdornment={
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          }
          placeholder={`${i18n.t("search")}...`}
          value={searchInput}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (!event.target.value) {
              // If they cleared the field, send a submit, as we will probably hide the Filter button
              onSubmit("");
            }
            setSearchInput(event.target.value);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            // This just looks for Enter to trigger filter like the Button
            if (event.key === 'Enter' || event.key === 'Return') {
              if (searchInput) {
                onSubmit();
              }
            }
          }}
        />
      </Box>

      {filterComponents.map((v: JSX.Element) => {
        // Prefix the filter with a remove button (a red X)
        return <div style={{ display: "flex" }} key={"fd" + v.key}>
          <IconButton
            size="small"
            style={{ width: "1.5rem", height: "1.5rem", marginRight: "0.5rem", marginTop: "1.5rem" }}
            onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => handleRemoveFilter(v.key?.toString() || "")} >
            <Clear color="error" />
          </IconButton>
          {v}
        </div>;
      })}

      {/* Build the Add Filter button, and the Filter button*/}
      <IconButton
        onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => handleAddFilter()}
        color="primary"
        component="span"
        size="small"
        disabled={filterComponents.length > 10}
        style={{ margin: 0, paddingLeft: 0, paddingBottom: 0, paddingTop: "1rem" }} >
        <AddCircle /> &nbsp;{i18n.t("filterable-table.add-filter")}
      </IconButton>

      {
        (filterComponents.length > 0 || searchInput) &&
        <Button
          onClick={handleFilterClick}
          disabled={!filterEnabled && !searchInput}
          variant="contained"
          size="small"
          style={{ marginTop: "1rem", marginLeft: "1rem" }}
          endIcon={<FilterAlt />} >
          {i18n.t("filter")}
        </Button>
      }
    </>
  )
}